// Copyright (c) Ping. All rights reserved.

import { THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT } from './types';
import type { ThreadParticipant, ThreadReply, ThreadSummary } from './types';

/**
 * Visible / overflow split for the participant strip. The strip caps at
 * `THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT` avatars; any extras roll up
 * into an opaque "+N" badge.
 */
export interface VisibleParticipants {
  readonly visible: readonly ThreadParticipant[];
  readonly overflowCount: number;
}

/**
 * Orders the recent participants by `lastActiveTimestamp` descending and
 * caps the visible list. Pure — used by both `ThreadInChat` and
 * `ThreadParticipantStrip`.
 */
export function visibleParticipants(
  participants: readonly ThreadParticipant[],
  limit: number = THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT,
): VisibleParticipants {
  if (limit <= 0) {
    return { visible: [], overflowCount: participants.length };
  }
  const sorted = [...participants].sort((a, b) => b.lastActiveTimestamp - a.lastActiveTimestamp);
  if (sorted.length <= limit) {
    return { visible: sorted, overflowCount: 0 };
  }
  return {
    visible: sorted.slice(0, limit),
    overflowCount: sorted.length - limit,
  };
}

/**
 * Build a `ThreadSummary` from a list of replies + the host-known
 * `closedByAuthor` flag. The host owns `unreadCount` (it depends on the
 * local read cursor); the helper returns 0 when no `unreadCount` is
 * provided so the in-chat affordance can show without it.
 */
export interface BuildThreadSummaryArgs {
  readonly identifier: ThreadSummary['identifier'];
  readonly replies: readonly ThreadReply[];
  readonly participants: readonly ThreadParticipant[];
  readonly unreadCount?: number;
  readonly closedByAuthor?: boolean;
}

export function buildThreadSummary(args: BuildThreadSummaryArgs): ThreadSummary {
  const { identifier, replies, participants } = args;
  const replyCount = replies.length;
  const lastReplyTimestamp =
    replyCount === 0 ? null : replies.reduce((acc, reply) => Math.max(acc, reply.timestamp), 0);
  const { visible } = visibleParticipants(participants);
  return {
    identifier,
    replyCount,
    unreadCount: args.unreadCount ?? 0,
    recentParticipants: visible,
    lastReplyTimestamp,
    closedByAuthor: args.closedByAuthor ?? false,
  };
}
